import { useState } from "react"
import { createPortal } from "react-dom"

import { initials, MAX_ASSIGNEES } from "@/lib/trainDom"

export interface AssignState {
  li: Element
  current: string[]
}

/** Pick up to two assignees for a task from the team roster. Optional — a task
 *  can have none. */
export function AssigneePicker({
  state,
  roster,
  onApply,
  onCancel,
}: {
  state: AssignState
  roster: { name: string }[]
  onApply: (names: string[]) => void
  onCancel: () => void
}) {
  const [selected, setSelected] = useState<string[]>(state.current)

  const toggle = (name: string) => {
    setSelected((sel) =>
      sel.includes(name)
        ? sel.filter((n) => n !== name)
        : sel.length < MAX_ASSIGNEES
          ? [...sel, name]
          : sel,
    )
  }

  const atMax = selected.length >= MAX_ASSIGNEES

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-xs flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-800">Assignees</h3>
          <span className="text-[11px] text-slate-400">
            {selected.length}/{MAX_ASSIGNEES}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {roster.map(({ name }) => {
            const on = selected.includes(name)
            const disabled = !on && atMax
            return (
              <button
                key={name}
                onClick={() => toggle(name)}
                disabled={disabled}
                className={`flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm transition ${
                  on ? "bg-emerald-50 text-slate-800" : disabled ? "text-slate-300" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                <span
                  className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full text-[10px] font-bold ${
                    on ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {initials(name)}
                </span>
                <span className="truncate">{name}</span>
                {on && <span className="ml-auto text-emerald-600">✓</span>}
              </button>
            )
          })}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
          <button
            onClick={() => setSelected([])}
            className="text-xs font-semibold text-slate-500 hover:text-slate-700"
          >
            Clear
          </button>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onApply(selected)}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
